// Simple Book Processing Service
// Basic text processing without external AI dependencies

/**
 * Generate a simple summary for a book
 * @param {string} fullText - The complete text content to summarize
 * @returns {Promise<string>} - Simple text-based summary
 */
export async function summarizeBook(fullText) {
  try {
    console.log('📝 [SUMMARIZER] Creating simple summary...');
    console.log('📝 [SUMMARIZER] Input text length:', fullText.length, 'characters');
    
    // Validate input
    if (!fullText || fullText.trim().length === 0) {
      throw new Error('Text content is required for summarization');
    }

    // Create a simple summary from the first few sentences
    const words = fullText.split(/\s+/).slice(0, 100).join(' ');
    const summary = `${words}...`;
    
    console.log('✅ [SUMMARIZER] Simple summary created');
    console.log('📝 [SUMMARIZER] Summary length:', summary.length, 'characters');
    
    return summary;
    
  } catch (error) {
    console.error('❌ [SUMMARIZER] Summary creation failed:', error);
    return 'Summary could not be generated at this time.';
  }
}

/**
 * Extract basic metadata from book content
 * @param {string} bookTitle - The book title
 * @param {string} summary - The book summary
 * @param {string} fullText - The complete text content
 * @returns {Promise<Object>} - Basic metadata
 */
export async function extractMetadata(bookTitle, summary, fullText) {
  try {
    console.log('🏷️ [METADATA] Creating basic metadata...');
    console.log('🏷️ [METADATA] Book title:', bookTitle);
    console.log('🏷️ [METADATA] Summary length:', summary.length, 'characters');
    
    // Validate input
    if (!summary || !fullText) {
      throw new Error('Summary and full text are required for metadata extraction');
    }

    // Generate basic metadata
    const metadata = {
      genre: 'General',
      characters: [
        {
          name: 'Narrator',
          gender: 'unknown',
          country: 'US',
          reasoning: 'Default narrator for audiobook',
          assigned_voice: 'Amy'
        }
      ],
      plot_moments: [
        {
          summary: summary.length > 200 ? summary.substring(0, 200) + '...' : summary
        }
      ],
      dialogue_segments: [
        {
          character: 'Narrator',
          text: summary.substring(0, 100) + '...'
        }
      ]
    };
    
    console.log('✅ [METADATA] Basic metadata created');
    console.log('🏷️ [METADATA] Genre:', metadata.genre);
    console.log('🏷️ [METADATA] Characters found:', metadata.characters?.length || 0);
    console.log('🏷️ [METADATA] Plot moments found:', metadata.plot_moments?.length || 0);
    
    return metadata;
    
  } catch (error) {
    console.error('❌ [METADATA] Metadata creation failed:', error);
    
    // Return minimal metadata
    return {
      genre: 'General',
      characters: [
        {
          name: 'Narrator',
          gender: 'unknown',
          country: 'US',
          reasoning: 'Default narrator',
          assigned_voice: 'Amy'
        }
      ],
      plot_moments: [
        {
          summary: 'Story content processed'
        }
      ],
      dialogue_segments: [
        {
          character: 'Narrator',
          text: 'Content ready for audiobook creation'
        }
      ]
    };
  }
}

/**
 * Process a book file with simple text processing
 * @param {File} file - The book file to process
 * @param {string} bookId - The book ID
 * @returns {Promise<Object>} - Processed book data
 */
export async function processBook(file, bookId) {
  console.log('🚀 [BOOK PROCESSOR] Starting simple book processing...');
  console.log('🚀 [BOOK PROCESSOR] File:', file.name);
  console.log('🚀 [BOOK PROCESSOR] Book ID:', bookId);
  
  try {
    // Step 1: Parse the file
    console.log('🚀 [BOOK PROCESSOR] Step 1: Parsing file...');
    const { parseFile } = await import('./fileParser.js');
    const parsedData = await parseFile(file);
    
    console.log('✅ [BOOK PROCESSOR] File parsing completed');
    console.log('🚀 [BOOK PROCESSOR] Text length:', parsedData.fullText.length);
    console.log('🚀 [BOOK PROCESSOR] Chapters:', parsedData.chapters.length);
    
    // Step 2: Generate simple summary
    console.log('🚀 [BOOK PROCESSOR] Step 2: Creating simple summary...');
    const summary = await summarizeBook(parsedData.fullText);
    console.log('✅ [BOOK PROCESSOR] Summary completed');
    
    // Step 3: Extract basic metadata
    console.log('🚀 [BOOK PROCESSOR] Step 3: Creating basic metadata...');
    const metadata = await extractMetadata(file.name, summary, parsedData.fullText);
    console.log('✅ [BOOK PROCESSOR] Metadata creation completed');
    
    const result = {
      summary,
      metadata,
      chapters: parsedData.chapters
    };
    
    console.log('✅ [BOOK PROCESSOR] Simple processing completed successfully');
    return result;
    
  } catch (error) {
    console.error('❌ [BOOK PROCESSOR] Processing error:', error);
    throw error;
  }
}